/**
 * crm/CommandPalette.jsx — Phase 75 refactor
 * Extracted from CRMShell.jsx (Phase 74). Cmd+K / Ctrl+K global search across
 * prospects / leads / deals / clients. Debounced 200ms.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { inr } from "./_shared";

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Global hotkey: Cmd+K (mac) or Ctrl+K (windows).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((x) => !x);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) { setResults(null); setQ(""); return; }
    if (q.trim().length < 2) { setResults(null); return; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/crm/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(handle);
  }, [q, open]);

  const jump = (path) => { setOpen(false); navigate(path); };

  if (!open) return null;
  const sections = results ? [
    ["Prospects",       "prospects", results.prospects.map((p) => ({ id: p.id, primary: p.full_name, secondary: p.company_name, path: `/crm?tab=prospects` }))],
    ["Leads",           "leads",     results.leads.map((l) => ({ id: l.id, primary: l.company_name, secondary: `${l.code || ""} · ${l.contact_name || ""}`, path: `/crm?tab=leads` }))],
    ["Deals",           "deals",     results.deals.map((d) => ({ id: d.id, primary: d.company_name, secondary: `${d.stage} · ${inr(d.proposal_value)}`, path: `/crm?tab=deals` }))],
    ["Clients",         "clients",   results.clients.map((c) => ({ id: c.id, primary: c.company_name, secondary: c.health, path: `/crm?tab=clients` }))],
  ] : [];

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-24 px-4" onClick={() => setOpen(false)} data-testid="crm-cmdk-palette">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[70vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <Search size={14} className="text-slate-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search prospects · leads · deals · clients…"
            className="flex-1 text-sm outline-none border-0 bg-transparent"
            data-testid="crm-cmdk-input"
          />
          <kbd className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">esc</kbd>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!results && q.trim().length < 2 && (
            <div className="p-6 text-xs text-slate-400 italic text-center">Type at least 2 characters to search.</div>
          )}
          {loading && <div className="p-3 text-[11px] text-slate-400 italic">Searching…</div>}
          {results && sections.every(([, , items]) => items.length === 0) && (
            <div className="p-6 text-xs text-slate-400 italic text-center">No matches for &quot;{q}&quot;.</div>
          )}
          {results && sections.map(([label, kind, items]) => items.length === 0 ? null : (
            <div key={kind} data-testid={`crm-cmdk-section-${kind}`}>
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => jump(it.path)}
                  className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center justify-between gap-3"
                  data-testid={`crm-cmdk-result-${it.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">{it.primary || "(no name)"}</div>
                    {it.secondary && <div className="text-[10px] text-slate-500 truncate">{it.secondary}</div>}
                  </div>
                  <ArrowRight size={11} className="text-slate-300 shrink-0" />
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-slate-200 text-[10px] text-slate-400 flex items-center justify-between">
          <span>Cmd+K · Ctrl+K to toggle</span>
          {results && <span>{sections.reduce((s, [,, items]) => s + items.length, 0)} results</span>}
        </div>
      </div>
    </div>
  );
}
